import { useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { SiteLayout } from "./SiteLayout";

// Public contact form: the operator's email is never shown on the website.
const TOPICS = [
  { value: "sales", label: "Buying Trim Log" },
  { value: "billing", label: "Billing or my subscription" },
  { value: "support", label: "Help using the app" },
  { value: "privacy", label: "Privacy or data request" },
  { value: "other", label: "Something else" },
];

export function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", topic: "support", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [key]: e.target.value });

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError("");
    try {
      const response = await fetch("/api/contact", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body.ok) { setError(body.error || "Your message could not be sent. Please try again."); return; }
      setSent(true);
    } catch { setError("Could not reach Trim Log. Check your connection and try again."); }
    finally { setBusy(false); }
  }

  return <SiteLayout title="Contact | Trim Log" description="Questions about Trim Log, billing or your data? Send us a message."><main id="main-content" className="account-main"><div className="account-story"><h1>Questions?<br />Ask away.</h1><p>Buying, billing, getting your crew set up, or a privacy request. Send a message and we’ll reply by email.</p><div className="account-note"><span aria-hidden="true">✓</span><p>Crew member who can’t get in? Ask your manager to send you a new invite from the console.</p></div></div>
    <div className="account-panel">
      {sent ? <><h2>Message sent.</h2><p role="status">Thanks. We’ll reply to the email address you gave.</p><Link to="/tour" className="site-button">Back to the product tour</Link></> : <>
        <h2>Contact us</h2><p>All fields are required.</p>
        <form onSubmit={submit} className="account-form">
          <div><label htmlFor="contact-name">Your name</label><input id="contact-name" name="name" autoComplete="name" maxLength={100} value={form.name} onChange={set("name")} required /></div>
          <div><label htmlFor="contact-email">Email address</label><input id="contact-email" name="email" type="email" autoComplete="email" maxLength={254} value={form.email} onChange={set("email")} required placeholder="you@yourbusiness.com" /></div>
          <div><label htmlFor="contact-topic">What’s it about?</label><select id="contact-topic" name="topic" value={form.topic} onChange={set("topic")}>{TOPICS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
          <div><label htmlFor="contact-message">Message</label><textarea id="contact-message" name="message" rows={6} minLength={10} maxLength={5000} value={form.message} onChange={set("message")} required /></div>
          {/* Spam trap: hidden from people, filled by bots. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}><label htmlFor="contact-website">Website</label><input id="contact-website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></div>
          {error && <p className="site-form-error" role="alert">{error}</p>}
          <button type="submit" disabled={busy} className="site-button">{busy ? "Sending…" : "Send message"}</button>
        </form>
      </>}
    </div>
  </main></SiteLayout>;
}
